import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { servicesApi } from '../lib/api';
import toast from 'react-hot-toast';
import { Plus, Edit, Trash2, Eye, ArrowUp, ArrowDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';

const ServiceList: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');

  const { data: services = [], isLoading } = useQuery({
    queryKey: ['services', i18n.language],
    queryFn: async () => {
      try {
        const response = await servicesApi.getAll(i18n.language);
        console.log('Services API Response:', response.data); // Debug log
        // Handle new response structure: { status: "success", data: [...] }
        // or fallback to old structure
        if (response.data?.status === 'success' && Array.isArray(response.data?.data)) {
          console.log('Extracted services:', response.data.data); // Debug log
          return response.data.data;
        }
        // Fallback for old structure or direct data
        const extracted = response.data?.data || response.data || [];
        console.log('Fallback extracted services:', extracted); // Debug log
        return Array.isArray(extracted) ? extracted : [];
      } catch (error) {
        console.error('Error fetching services:', error);
        return [];
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => servicesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['services'] });
      toast.success(t('serviceDeleted'));
    },
    onError: () => {
      toast.error(t('errorOccurred'));
    },
  });

  const filteredServices = services.filter((service: any) => {
    const title = i18n.language === 'ar' ? service.title_ar : service.title_en;
    const subtitle = i18n.language === 'ar' ? service.subtitle_ar : service.subtitle_en;
    const searchLower = searchTerm.toLowerCase();
    return (
      title?.toLowerCase().includes(searchLower) ||
      subtitle?.toLowerCase().includes(searchLower) ||
      service.title_en?.toLowerCase().includes(searchLower) ||
      service.title_ar?.toLowerCase().includes(searchLower) ||
      service.subtitle_en?.toLowerCase().includes(searchLower) ||
      service.subtitle_ar?.toLowerCase().includes(searchLower)
    );
  });

  const handleDelete = (id: string) => {
    deleteMutation.mutate(id);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">
          {i18n.language === 'ar' ? 'إدارة الخدمات' : 'Services Management'}
        </h1>
        <Button onClick={() => navigate('/services/new')}>
          <Plus className="h-4 w-4 mr-2" />
          {i18n.language === 'ar' ? 'إضافة خدمة' : 'Add Service'}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{i18n.language === 'ar' ? 'قائمة الخدمات' : 'Services List'}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <Input
              placeholder={i18n.language === 'ar' ? 'البحث في الخدمات...' : 'Search services...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-sm"
            />
          </div>

          <div className={`overflow-x-auto ${i18n.language === 'ar' ? 'rtl' : 'ltr'}`}>
            <Table className={i18n.language === 'ar' ? '[&_th]:text-right [&_td]:text-right' : '[&_th]:text-left [&_td]:text-left'}>
              <TableHeader>
                <TableRow>
                  <TableHead>{i18n.language === 'ar' ? 'الترتيب' : 'Order'}</TableHead>
                  <TableHead>{i18n.language === 'ar' ? 'العنوان' : 'Title'}</TableHead>
                  <TableHead>{i18n.language === 'ar' ? 'العنوان الفرعي' : 'Subtitle'}</TableHead>
                  <TableHead>{i18n.language === 'ar' ? 'الوصف' : 'Description'}</TableHead>
                  <TableHead>{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredServices.map((service: any) => (
                  <TableRow key={service.id}>
                    <TableCell>
                      <Badge variant="outline" className="font-mono">
                        {service.order || 0}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">
                        {i18n.language === 'ar' 
                          ? (service.title_ar || service.title_en || 'بدون عنوان')
                          : (service.title_en || service.title_ar || 'No title')
                        }
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-gray-600">
                        {i18n.language === 'ar' 
                          ? (service.subtitle_ar || service.subtitle_en || 'بدون عنوان فرعي')
                          : (service.subtitle_en || service.subtitle_ar || 'No subtitle')
                        }
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-gray-500 truncate max-w-xs">
                        {i18n.language === 'ar' 
                          ? (service.description_part1_ar || service.description_part1_en || 'بدون وصف')
                          : (service.description_part1_en || service.description_part1_ar || 'No description')
                        }
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2 rtl:space-x-reverse">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/services/${service.id}`)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/services/${service.id}/edit`)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="destructive" size="sm">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                {i18n.language === 'ar' ? 'تأكيد الحذف' : 'Confirm Delete'}
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                {i18n.language === 'ar' 
                                  ? 'هل أنت متأكد من حذف هذه الخدمة؟ لا يمكن التراجع عن هذا الإجراء.'
                                  : 'Are you sure you want to delete this service? This action cannot be undone.'
                                }
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>
                                {i18n.language === 'ar' ? 'إلغاء' : 'Cancel'}
                              </AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(service.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                {i18n.language === 'ar' ? 'حذف' : 'Delete'}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {filteredServices.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              {i18n.language === 'ar' ? 'لا توجد خدمات' : 'No services found'}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ServiceList;
